import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import Overview from '../components/Dashboard/Overview';
import ConnectBrokerage from '../components/Trading/ConnectBrokerage';
import WebullConnect from '../components/Trading/WebullConnect';
import MastersList from '../components/Trading/MastersList';
import ActiveCopies from '../components/Trading/ActiveCopies';
import OrdersList from '../components/Trading/OrdersList';
import Settings from '../components/Dashboard/Settings';
import ProfitLoss from '../components/Dashboard/ProfitLoss';
import StockAnalysisChatbot from '../components/Trading/StockAnalysisChatbot';
import TermsTemplates from '../components/Admin/TermsTemplates';
import EodReports from '../components/Admin/EodReports';
import AdminOpenPositions from '../components/Admin/AdminOpenPositions';
import MasterFollowersOpenPositions from '../components/Dashboard/MasterFollowersOpenPositions';
import TopTraderLeaderboard from '../components/Dashboard/TopTraderLeaderboard';
import CreateSignalModal from '../components/Community/CreateSignalModal';
import { useAuthStore } from '../stores';
import { useApi } from '../hooks/useApi';
import { Loader } from '../components/Common/Loader';
import { showToast } from '../utils/toast';
import { formatTradeErrorForDisplay } from '../utils/formatTradeError';
import { socketService } from '../services/socketService';
import AdminPlatformProfitLoss from '../components/Admin/AdminPlatformProfitLoss';
// import AdminMasterFees from '../components/Admin/AdminMasterFees';
import AdminAffiliateManagement from '../components/Admin/AdminAffiliateManagement';
import AdminMasterTraderApplications from '../components/Admin/AdminMasterTraderApplications';
import AdminMasterFollowingReport from '../components/Admin/AdminMasterFollowingReport';
import PricingPlans from '../components/Dashboard/PricingPlans';
import AffiliateProgram from '../components/Dashboard/AffiliateProgram';
import {
  canAccessSubscriptionGatedFeatures,
  isSubscriptionGatedTab,
  hidesBillingTab,
} from '../utils/subscriptionAccess';
import SubscriptionStatusAlert from '../components/Common/SubscriptionStatusAlert';
import DiscordOnboardingModal from '../components/Common/DiscordOnboardingModal';
import DiscordOnboarding from '../components/Dashboard/DiscordOnboarding';
import { DiscordPlanKey, isDiscordPlan } from '../utils/discordInvites';
import CalculatorPage from './CalculatorPage';

// Lazy load admin components
const SubscriptionManagement = lazy(() => import('../components/Admin/SubscriptionManagement'));
const VALID_TABS = new Set([
  'overview',
  'pricing',
  'connect',
  'webull',
  'masters',
  'calculator',
  'leaderboard',
  'affiliate',
  'activeCopies',
  'trades',
  'profit-loss',
  'ai-chat',
  'terms',
  'Eod Reports',
  'platform-profit-loss',
  'admin-profits-losses',
  // 'master-fees',
  'affiliate-management',
  'master-applications',
  'master-following-report',
  'openPositions',
  'subscriptions-all',
  'subscriptions-active',
  'subscriptions-expired',
  'subscriptions-none',
  'settings',
  'discord',
]);

const DashboardPage: React.FC = () => {
  const { user, logout, setUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [hasHandledCallback, setHasHandledCallback] = useState(false);
  const [discordOnboardingPlan, setDiscordOnboardingPlan] = useState<DiscordPlanKey | null>(null);

  // Broker selection state - persisted to localStorage
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(() => {
    return localStorage.getItem('selectedBrokerId') || null;
  });

  const queryClient = useQueryClient();

  // Handler for broker change - persists to localStorage
  const handleBrokerChange = useCallback((brokerId: string | null) => {
    setSelectedBrokerId(brokerId);
    if (brokerId) {
      localStorage.setItem('selectedBrokerId', brokerId);
    } else {
      localStorage.removeItem('selectedBrokerId');
    }
    queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'masters'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [queryClient, selectedBrokerId]);
  const { data: dashboardData, isLoading, refetch } = useApi.useDashboard(selectedBrokerId);
  useEffect(() => {
    if (selectedBrokerId !== null && selectedBrokerId !== undefined) {
      refetch();
    }
  }, [selectedBrokerId, refetch]);

  const { data: userData, refetch: refetchUser } = useApi.useCurrentUser();
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useApi.useSubscriptionStatus({
    enabled: user?.role === 'follower',
  });

  const currentUser = user || dashboardData?.user;
  const isFollower = currentUser?.role === 'follower';
  const isCheckingSubscription = isFollower && isLoadingSubscription;
  const subscriptionResolved = !isFollower || !isLoadingSubscription;
  const canAccessGatedTabs = canAccessSubscriptionGatedFeatures(
    currentUser,
    subscriptionData,
  );

  const navigateToTab = useCallback(
    (nextTab: string) => {
      if (nextTab === 'affiliate' && !currentUser?.ableToAffiliate) {
        setActiveTab('overview');
        navigate('/dashboard/overview');
        return;
      }
      const partnerOnly =
        !!currentUser?.ableToAffiliate &&
        currentUser?.affiliateClientAccess === false &&
        currentUser?.role !== 'admin';
      if (partnerOnly && nextTab !== 'affiliate') {
        setActiveTab('affiliate');
        navigate('/dashboard/affiliate');
        return;
      }
      const planNoneOnly =
        currentUser?.role === 'follower' &&
        (!currentUser?.plan || currentUser.plan === 'none');
      if (planNoneOnly && nextTab !== 'pricing') {
        setActiveTab('pricing');
        navigate('/dashboard/pricing');
        return;
      }
      setActiveTab(nextTab);
      navigate(`/dashboard/${nextTab}`);
    },
    [navigate, currentUser?.role, currentUser?.plan, currentUser?.ableToAffiliate, currentUser?.affiliateClientAccess],
  );

  useEffect(() => {
    if (!subscriptionResolved) return;

    const pathParts = location.pathname.split('/').filter(Boolean);
    const pathTab = pathParts[1] || '';
    const hideBilling = hidesBillingTab(currentUser?.plan);
    const planNoneOnly =
      currentUser?.role === 'follower' &&
      (!currentUser?.plan || currentUser.plan === 'none');
    const planNoneAllowed = new Set(['pricing']);

    if (pathTab === 'affiliate' && !currentUser?.ableToAffiliate) {
      setActiveTab('overview');
      navigate('/dashboard/overview', { replace: true });
      return;
    }

    const partnerOnly =
      !!currentUser?.ableToAffiliate &&
      currentUser?.affiliateClientAccess === false &&
      currentUser?.role !== 'admin';
    if (partnerOnly && pathTab !== 'affiliate') {
      setActiveTab('affiliate');
      navigate('/dashboard/affiliate', { replace: true });
      return;
    }

    if (hideBilling && (pathTab === 'pricing' || pathTab === 'discord')) {
      setActiveTab('overview');
      navigate('/dashboard/overview', { replace: true });
      return;
    }

    if (planNoneOnly && pathTab && !planNoneAllowed.has(pathTab)) {
      setActiveTab('pricing');
      navigate('/dashboard/pricing', { replace: true });
      return;
    }

    if (pathTab && VALID_TABS.has(pathTab)) {
      // TEMP: subscription toast / redirect disabled
      // if (isSubscriptionGatedTab(pathTab) && !canAccessGatedTabs) {
      //   showToast('error', 'An active subscription is required to access this page.');
      //   setActiveTab('pricing');
      //   navigate('/dashboard/pricing', { replace: true });
      //   return;
      // }
      setActiveTab(pathTab);
      return;
    }
    const legacyTab = new URLSearchParams(location.search).get('tab');
    if (legacyTab && VALID_TABS.has(legacyTab)) {
      if (legacyTab === 'affiliate' && !currentUser?.ableToAffiliate) {
        setActiveTab('overview');
        navigate('/dashboard/overview', { replace: true });
        return;
      }
      if (hideBilling && (legacyTab === 'pricing' || legacyTab === 'discord')) {
        setActiveTab('overview');
        navigate('/dashboard/overview', { replace: true });
        return;
      }
      if (planNoneOnly && !planNoneAllowed.has(legacyTab)) {
        setActiveTab('pricing');
        navigate('/dashboard/pricing', { replace: true });
        return;
      }
      // TEMP: subscription toast / redirect disabled
      // if (isSubscriptionGatedTab(legacyTab) && !canAccessGatedTabs) {
      //   showToast('error', 'An active subscription is required to access this page.');
      //   setActiveTab('pricing');
      //   navigate('/dashboard/pricing', { replace: true });
      //   return;
      // }
      setActiveTab(legacyTab);
      navigate(`/dashboard/${legacyTab}`, { replace: true });
      return;
    }
    if (planNoneOnly) {
      setActiveTab('pricing');
      navigate('/dashboard/pricing', { replace: true });
      return;
    }
    setActiveTab('overview');
    if (location.pathname === '/dashboard' || !pathTab) {
      navigate('/dashboard/overview', { replace: true });
    }
  }, [location.pathname, location.search, navigate, canAccessGatedTabs, subscriptionResolved, currentUser?.plan, currentUser?.role, currentUser?.ableToAffiliate, currentUser?.affiliateClientAccess]);

  // Update user in store when dashboard data or userData changes (consolidated)
  useEffect(() => {
    if (userData?.user) {
      setUser(userData.user);
    } else if (dashboardData?.user && !userData) {
      setUser(dashboardData.user);
    }
  }, [dashboardData?.user, userData?.user, setUser]);

  const handleRefresh = useCallback(() => {
    refetch();
    refetchUser();
  }, [refetch, refetchUser]);

  // After SnapTrade/Webull OAuth — open account picker on Connect tab (don't auto-connect all)
  useEffect(() => {
    if (hasHandledCallback) return;
    if (!user && !userData?.user) return;

    const params = new URLSearchParams(window.location.search);
    const snaptradeCallback = params.get('snaptrade_callback');

    if (snaptradeCallback === 'true' || snaptradeCallback === '1') {
      setHasHandledCallback(true);
      try {
        sessionStorage.setItem('webull_pending_account_select', '1');
      } catch {
        /* ignore */
      }
      window.history.replaceState({}, '', '/dashboard/connect');
      navigateToTab('connect');
      window.dispatchEvent(new CustomEvent('webull:select-accounts'));
      showToast('info', 'Select which Webull account to connect…');
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    }
  }, [hasHandledCallback, user, userData, navigateToTab, queryClient]);

  // Check for subscription success/cancel in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionStatus = params.get('subscription');
    const purchasedPlan = String(params.get('plan') || '').toLowerCase();

    if (subscriptionStatus === 'success') {
      showToast('success', 'Payment successful! Your subscription will be activated shortly.');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      handleRefresh();
      if (isDiscordPlan(purchasedPlan)) {
        setDiscordOnboardingPlan(purchasedPlan);
      }
      const legacyReturn = ['og', 'special_users', 'old_user', 'full_access_paid'].includes(purchasedPlan);
      window.history.replaceState({}, '', legacyReturn ? '/dashboard' : '/dashboard/pricing');
      if (legacyReturn) setActiveTab('overview');
    } else if (subscriptionStatus === 'cancelled') {
      showToast('error', 'Payment was cancelled. Please try again.');
      window.history.replaceState({}, '', '/dashboard/pricing');
    }
  }, [queryClient, handleRefresh]);
  useEffect(() => {
    if (!user?.id && !user?._id) return;

    // Listen for new trades
    const handleNewTrade = (data: any) => {
      console.log('📈 New trade received:', data);
      showToast('success', `Trade executed: ${data.trade?.side?.toUpperCase()} ${data.trade?.qty} ${data.trade?.symbol}`);
      handleRefresh();
    };

    // Listen for copy trade execution
    const handleCopyExecuted = (data: any) => {
      console.log('📋 Copy trade executed:', data);
      showToast('success', `Copy trade: ${data.copyTrade?.side?.toUpperCase()} ${data.copyTrade?.qty} ${data.copyTrade?.symbol}`);
      handleRefresh();
    };

    // Listen for copy trade failures
    const handleCopyFailed = (data: any) => {
      console.log('❌ Copy trade failed:', data);
      const friendlyError = formatTradeErrorForDisplay(data.error);
      showToast('error', `Copy trade failed: ${friendlyError}`);
    };

    // Listen for dashboard updates
    const handleDashboardUpdate = () => {
      console.log('🔄 Dashboard update received');
      refetch();
    };

    // Listen for trade status changes
    const handleTradeStatus = (data: any) => {
      console.log('📊 Trade status update:', data);
      refetch();
    };

    socketService.on('trade:new', handleNewTrade);
    socketService.on('copy:executed', handleCopyExecuted);
    socketService.on('copy:failed', handleCopyFailed);
    socketService.on('dashboard:update', handleDashboardUpdate);
    socketService.on('trade:status', handleTradeStatus);

    return () => {
      socketService.off('trade:new', handleNewTrade);
      socketService.off('copy:executed', handleCopyExecuted);
      socketService.off('copy:failed', handleCopyFailed);
      socketService.off('dashboard:update', handleDashboardUpdate);
      socketService.off('trade:status', handleTradeStatus);
    };
  }, [user?.id, user?._id, refetch]);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (!user) {
    return null;
  }

  const displayUser = user;
  const plansOnly =
    displayUser.role === 'follower' &&
    (!displayUser.plan || displayUser.plan === 'none');

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header
        user={displayUser}
        onLogout={handleLogout}
        showSidebarToggle={!plansOnly}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
      />

      <div className="flex relative pt-0 lg:pt-0">
        {!plansOnly && (
          <Sidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            user={displayUser}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
            selectedBrokerId={selectedBrokerId}
            onBrokerChange={handleBrokerChange}
            subscriptionResolved={subscriptionResolved}
            canAccessGatedTabs={canAccessGatedTabs}
          />
        )}

        {!plansOnly && isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className={`app-main flex-1 px-4 py-4 sm:px-6 lg:px-6 overflow-x-hidden min-h-[calc(100vh-4rem)] ${plansOnly ? '' : 'lg:ml-72'}`}>
          <div className="hidden pt-2 pb-4">
            <nav className="flex gap-2 overflow-x-auto pb-2">
              {['overview', 'connect', 'masters', 'activeCopies', 'settings']
                .filter((tab) => {
                  if (displayUser.role === 'admin') {
                    return ['overview', 'subscriptions-all', 'terms', 'settings'].includes(tab);
                  }
                  if (displayUser.role === 'master') {
                    return ['overview', 'connect', 'followers', 'settings'].includes(tab);
                  }
                  if (!canAccessGatedTabs && isSubscriptionGatedTab(tab)) {
                    return false;
                  }
                  return !tab.startsWith('subscriptions');
                })
                .map((tab) => (
                  <button
                    key={tab}
                    onClick={() => navigateToTab(tab)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${activeTab === tab || (tab === 'subscriptions-all' && activeTab.startsWith('subscriptions'))
                      ? 'bg-primary text-white shadow-glow'
                      : 'bg-surface text-muted hover:text-primary'
                      }`}
                  >
                    {tab === 'activeCopies'
                      ? 'Active Copies'
                      : tab === 'connect'
                        ? (displayUser.hasBrokerageConnected || displayUser.isAlpacaConnected ? 'Brokerage Accounts' : 'Connect Brokerage')
                        : tab === 'ai-chat'
                          ? 'AI Strategy'
                          : tab === 'terms'
                            ? 'Terms Templates'
                            : tab === 'subscriptions-all'
                              ? 'Subscriptions'
                              : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
            </nav>
          </div>

          <div className="lg:bg-transparent overflow-x-hidden min-h-[calc(100vh-5rem)]">
            {isCheckingSubscription ? (
              <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] w-full">
                <Loader fullScreen={false} />
              </div>
            ) : isLoading && !dashboardData ? (
              <Loader fullScreen={false} message="Loading dashboard..." />
            ) : plansOnly ? (
              <div className="space-y-6 overflow-x-hidden">
                <PricingPlans
                  user={displayUser}
                  onPlatformPurchase={(plan) => {
                    if (isDiscordPlan(plan)) setDiscordOnboardingPlan(plan);
                  }}
                />
              </div>
            ) : (
              <div className="space-y-6 overflow-x-hidden">
                {/* TEMP: hide subscription required banner */}
                {/* {isFollower && (
                  <SubscriptionStatusAlert subscriptionData={subscriptionData} />
                )} */}
                {activeTab === 'overview' && (
                  <Overview
                    data={dashboardData || null}
                    user={displayUser}
                    refresh={handleRefresh}
                    selectedBrokerId={selectedBrokerId}
                  />
                )}
                {activeTab === 'pricing' && (
                  <PricingPlans
                    user={displayUser}
                    onPlatformPurchase={(plan) => {
                      if (isDiscordPlan(plan)) setDiscordOnboardingPlan(plan);
                    }}
                  />
                )}
                {activeTab === 'discord' && isDiscordPlan(displayUser.plan) && (
                  <DiscordOnboarding user={displayUser} />
                )}
                {activeTab === 'connect' && canAccessGatedTabs && (
                  <ConnectBrokerage
                    user={displayUser}
                    refresh={handleRefresh}
                    selectedBrokerId={selectedBrokerId}
                    onBrokerChange={handleBrokerChange}
                  />
                )}
                {activeTab === 'webull' && displayUser.role === 'master' && (
                  <WebullConnect />
                )}
                {activeTab === 'masters' && displayUser.role === 'follower' && (
                  <MastersList user={displayUser} refresh={handleRefresh} selectedBrokerId={selectedBrokerId} />
                )}
                {activeTab === 'calculator' && (
                  <CalculatorPage selectedBrokerId={selectedBrokerId} />
                )}
                {activeTab === 'activeCopies' && displayUser.role === 'follower' && canAccessGatedTabs && (
                  <ActiveCopies selectedBrokerId={selectedBrokerId} />
                )}
                {activeTab === 'leaderboard' && (
                  <TopTraderLeaderboard />
                )}
                {activeTab === 'affiliate' && displayUser.ableToAffiliate && <AffiliateProgram />}
                {activeTab === 'trades' && <OrdersList selectedBrokerId={selectedBrokerId} />}
                {activeTab === 'profit-loss' && <ProfitLoss user={currentUser} selectedBrokerId={selectedBrokerId} />}
                {activeTab === 'ai-chat' && canAccessGatedTabs && <StockAnalysisChatbot />}
                {activeTab === 'terms' && displayUser.role === 'admin' && <TermsTemplates />}
                {activeTab === 'Eod Reports' && displayUser.role === 'admin' && <EodReports refresh={handleRefresh} />}
                {(activeTab === 'platform-profit-loss' || activeTab === 'admin-profits-losses') &&
                  displayUser.role === 'admin' && <AdminPlatformProfitLoss />}
                {/* {activeTab === 'master-fees' && displayUser.role === 'admin' && <AdminMasterFees />} */}
                {activeTab === 'affiliate-management' && displayUser.role === 'admin' && (
                  <AdminAffiliateManagement />
                )}
                {activeTab === 'master-applications' && displayUser.role === 'admin' && (
                  <AdminMasterTraderApplications />
                )}
                {activeTab === 'master-following-report' && displayUser.role === 'admin' && (
                  <AdminMasterFollowingReport />
                )}
                {activeTab === 'openPositions' && displayUser.role === 'admin' && <AdminOpenPositions />}
                {activeTab === 'openPositions' &&
                  (displayUser.role === 'master' || displayUser.role === 'master_pending') && (
                    <MasterFollowersOpenPositions />
                  )}

                {/* Admin Subscription Management Tabs */}
                {displayUser.role === 'admin' && activeTab === 'subscriptions-all' && (
                  <Suspense fallback={<Loader message="Loading..." />}>
                    <SubscriptionManagement initialFilter="" />
                  </Suspense>
                )}
                {displayUser.role === 'admin' && activeTab === 'subscriptions-active' && (
                  <Suspense fallback={<Loader message="Loading..." />}>
                    <SubscriptionManagement initialFilter="active" />
                  </Suspense>
                )}
                {displayUser.role === 'admin' && activeTab === 'subscriptions-expired' && (
                  <Suspense fallback={<Loader message="Loading..." />}>
                    <SubscriptionManagement initialFilter="expired" />
                  </Suspense>
                )}
                {displayUser.role === 'admin' && activeTab === 'subscriptions-none' && (
                  <Suspense fallback={<Loader message="Loading..." />}>
                    <SubscriptionManagement initialFilter="none" />
                  </Suspense>
                )}

                {activeTab === 'settings' && (
                  <Settings data={dashboardData || null} refresh={handleRefresh} />
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      <CreateSignalModal />
      {discordOnboardingPlan && (
        <DiscordOnboardingModal
          plan={discordOnboardingPlan}
          onClose={() => setDiscordOnboardingPlan(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
